import { Button } from "@/components/ui/button";
import { normalizeNotificationLink } from '@/lib/notificationLinks';
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from 'react-router';
import { Bell, Check, MessageSquare, Briefcase, DollarSign, Star, AlertCircle } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import type { AppNotification } from '@/hooks/useNotifications';

interface NotificationDropdownProps {
  profileMode?: 'helper' | 'client';
  instanceId?: string;
}

export function NotificationDropdown({ profileMode: _profileMode = 'client', instanceId: _instanceId = 'default' }: NotificationDropdownProps) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: AppNotification) => {
    markRead(notification.id);
    const link = normalizeNotificationLink(notification.link);
    if (link) {
      navigate(link);
    }
  };

  const getNotificationIcon = (type: string) => {
    if (type.startsWith('booking')) return <Briefcase className="h-4 w-4 text-blue-500" />;
    if (type.startsWith('message')) return <MessageSquare className="h-4 w-4 text-green-500" />;
    if (type.startsWith('payment')) return <DollarSign className="h-4 w-4 text-emerald-500" />;
    if (type.startsWith('review')) return <Star className="h-4 w-4 text-yellow-500" />;
    if (type === 'job' || type.startsWith('job')) return <Star className="h-4 w-4 text-yellow-500" />;
    return <AlertCircle className="h-4 w-4 text-purple-500" />;
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative h-10 w-10 overflow-visible text-white/80 hover:text-white hover:bg-white/10"
          aria-label={`Notifications. ${unreadCount} unread`}
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              className="pointer-events-none absolute -top-0.5 -right-0.5 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-kanfy-amber px-1 text-[10px] font-bold leading-none text-kanfy-on-amber ring-2 ring-kanfy-ink shadow-sm"
              aria-label={`${unreadCount} unread notifications`}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-background border border-border shadow-lg z-50">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                markAllRead();
              }}
              className="h-auto p-1 text-xs text-primary hover:text-primary"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <ScrollArea className="h-[400px]">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${
                  !notification.read ? 'bg-primary/5' : ''
                } ${
                  notification.type.startsWith('booking') && !notification.read
                    ? 'border-l-2 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                    : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 space-y-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-medium line-clamp-1 ${
                      !notification.read ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {notification.title}
                    </p>
                    {!notification.read && (
                      <Badge variant="secondary" className="h-2 w-2 p-0 bg-primary shrink-0"></Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {notification.body}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimeAgo(notification.created_at)}
                  </p>
                </div>
              </DropdownMenuItem>
            ))
          ) : (
            <div className="text-center py-8 px-4">
              <Bell className="h-8 w-8 mx-auto text-muted-foreground opacity-50 mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
